import { Dispatch, lazy, SetStateAction, Suspense, useState } from "react";

import Loading from "components/Loading";
import { INIT_ACCOUNT, INIT_DEPARTMENT } from "constants/filter";
import { SortDirection, sortDirection as SORT_DIRECTION } from "constants/sortDirection";
import useCancelOption from "hooks/api/useCancelOption";
import useFindOption from "hooks/api/useFindOption";
import useFindOptions from "hooks/api/useFindOptions";
import useGetUsers from "hooks/api/useGetUsers";
import { useSyncEffect } from "hooks/effects";
import { useDebounce } from "hooks/useDebounce";
import useErrorModalStatusContext from "hooks/useErrorModalStatusContext";
import { useGlobalDepartmentAccountFilter } from "hooks/useGlobalDepartmentAccountFilter";
import { useGoogleAnalytics } from "hooks/useGoogleAnalytics";
import { useInfoModal, useWarningModal } from "hooks/useModal";
import usePageNumberSearchParam from "hooks/usePageNumberSearchParam";
import usePageTransition from "hooks/usePageTransition";
import { Department, findUniqueDepartments } from "utils/accountUtils";
import { isBrowser, isTablet, isMobile } from "utils/deviceDetect";

const PAGE_SIZE = 20;

const OptionListPresenter = lazy(() =>
  isBrowser
    ? import("pages/OptionList/pc/OptionListPresenter")
    : isTablet
      ? import("pages/OptionList/pc/OptionListPresenter")
      : import("pages/OptionList/sp/OptionListPresenter")
);

export type OptionListPresenterProps = {
  sortDirection: SortDirection;
  orderBy?: "createdAt";
  searchKeyword: string;
  options: Option[] | undefined;
  currentPageNumber: number;
  isDrawerVisible: boolean;
  chosenAccount?: Account;
  chosenDepartment?: string;
  departmentList: Department[];
  accountList: Account[];
  filteredAccountList: Account[];
  totalPageCount: number;
  selectedOption?: Option;
  setCurrentPageNumber: Dispatch<SetStateAction<number>>;
  InfoModal: JSX.Element;
  WarningModal: JSX.Element;
  selectedOptionId?: string;
  handleChangeSort: () => void;
  setChosenAccount: (value: Account) => void;
  setChosenDepartment: (value: string) => void;
  handleChangeSearchKeyword: (v: string) => void;
  handleCancelOption: (id: string) => Promise<void>;
  handleNavigateEdit: (id: string) => void;
  handleNavigateOptionDetail: (optionId: string) => void;
  handleNavigateCreate: () => void;
  handleNavigateOrder: (id: string) => void;
  handleChangeSelectedOptionId: (id?: string) => void;
};

const OptionListContainer = () => {
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [currentPageNumber, setCurrentPageNumber] = usePageNumberSearchParam();
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [sortDirection, setSortDirection] = useState<SortDirection>(SORT_DIRECTION.desc);
  const [orderBy] = useState<"createdAt" | undefined>("createdAt");
  const [selectedOptionId, setSelectedOptionId] = useState<string | undefined>();
  const [infoModalState, setInfoModalState] = useState<{ title: string }>({ title: "" });
  const [warningModalState, setWarningModalState] = useState<{ title: string; label: string; onClick: () => void }>({
    title: "",
    label: "",
    onClick: () => null,
  });

  const { accountList } = useGetUsers();
  const { chosenDepartment, chosenAccount, setChosenDepartment, setChosenAccount, filteredAccountList, isFilterReady } =
    useGlobalDepartmentAccountFilter(accountList ?? []);
  const { debouncedValue: debouncedSearchKeyword } = useDebounce(searchKeyword, 500);
  const { sendEvent } = useGoogleAnalytics(true);
  const navigate = usePageTransition();
  const { showErrorModal } = useErrorModalStatusContext();
  const { options, error, pagination, mutate } = useFindOptions(
    isFilterReady
      ? {
          pageSize: PAGE_SIZE,
          pageNumber: currentPageNumber,
          orderType: sortDirection,
          orderBy: orderBy,
          contactId: chosenAccount?.id !== INIT_ACCOUNT.id ? chosenAccount?.id : undefined,
          department: chosenDepartment !== INIT_DEPARTMENT ? chosenDepartment : undefined,
          searchKeyword: debouncedSearchKeyword,
        }
      : null
  );
  const { option, mutate: mutateFindById } = useFindOption(selectedOptionId);
  const { cancelOption } = useCancelOption();
  const { showInfoModal, InfoModal } = useInfoModal();
  const { WarningModal, showWarningModal, hideWarningModal } = useWarningModal();

  const departmentList = findUniqueDepartments(accountList);

  const handleChangeSearchKeyword = (v: string) => {
    setCurrentPageNumber(1);
    setSearchKeyword(v);
  };

  const handleChangeSort = () => {
    setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    setCurrentPageNumber(1);
  };

  const handleNavigateOptionDetail = (optionId: string) => {
    navigate(`/option/${optionId}`);
  };

  //TODO: 詳細画面に移植
  const openCompleteModal = () => {
    setInfoModalState({
      title: "依頼を取り消しました",
    });
    showInfoModal();
  };

  //TODO: 詳細画面に移植
  const handleCancel = async (id: string) => {
    if (!selectedOptionId) return;
    const status = await cancelOption(id).catch(() => {
      showErrorModal({ httpMethod: "put" });
      hideWarningModal();
    });
    if (status === 200) {
      hideWarningModal();
      openCompleteModal();
      await mutate();
      await mutateFindById();
      // 依頼取消後に詳細モーダルを閉じる
      // PCはドロワーが常にローディング状態となってしまうため適用外
      if (isTablet || isMobile) {
        setSelectedOptionId(undefined);
      }
    }
  };

  //TODO: 詳細画面に移植
  const openSubmitModal = (id: string) => {
    setWarningModalState({
      title: "依頼を取り消しますか？",
      label: "取り消す",
      onClick: () => handleCancel(id),
    });
    showWarningModal();
  };

  const handleCancelOption = async (id: string) => {
    openSubmitModal(id);
  };

  const handleNavigateCreate = () => {
    navigate("/option/create");
  };

  const handleNavigateOrder = (id: string) => {
    navigate(`/order/${id}`);
  };

  const handleNavigateEdit = (id: string) => {
    sendEvent({
      action: "各種申請変更に遷移_リンク",
      category: "link",
      label: "/option",
    });
    navigate(`/option/${id}/update`);
  };

  useSyncEffect(() => {
    if (error) showErrorModal({ httpMethod: "get" });
  }, [error, showErrorModal]);

  return (
    <Suspense fallback={<Loading />}>
      <OptionListPresenter
        selectedOption={option}
        orderBy={orderBy}
        sortDirection={sortDirection}
        accountList={accountList}
        filteredAccountList={filteredAccountList}
        chosenAccount={chosenAccount}
        setChosenAccount={setChosenAccount}
        chosenDepartment={chosenDepartment}
        setChosenDepartment={setChosenDepartment}
        currentPageNumber={currentPageNumber}
        setCurrentPageNumber={setCurrentPageNumber}
        departmentList={departmentList}
        isDrawerVisible={isDrawerVisible}
        handleNavigateOptionDetail={handleNavigateOptionDetail}
        handleCancelOption={handleCancelOption}
        handleChangeSearchKeyword={handleChangeSearchKeyword}
        handleChangeSort={handleChangeSort}
        handleNavigateEdit={handleNavigateEdit}
        options={options}
        searchKeyword={searchKeyword}
        totalPageCount={pagination.totalPages ?? 1}
        handleNavigateCreate={handleNavigateCreate}
        handleNavigateOrder={handleNavigateOrder}
        InfoModal={<InfoModal title={infoModalState.title} />}
        handleChangeSelectedOptionId={(v) => setSelectedOptionId(v)}
        selectedOptionId={selectedOptionId}
        WarningModal={
          <WarningModal
            title={warningModalState.title}
            label={warningModalState.label}
            onClick={warningModalState.onClick}
          />
        }
      />
    </Suspense>
  );
};

export default OptionListContainer;
